import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

/**
 * Button variants using CSS Variables from ThemeContext
 * These will automatically update when theme changes
 */
const variants = {
  // Theme-aware variants (use CSS variables)
  primary: 'bg-[var(--button-primary)] text-[var(--button-primary-text)] hover:opacity-90 border-transparent',
  secondary: 'bg-[var(--bg-secondary)] text-[var(--text-primary)] border-[var(--border-color)] hover:bg-[var(--bg-hover)]',

  // Fixed color variants
  outline: 'bg-transparent text-[var(--text-primary)] border-[var(--border-color)] hover:bg-[var(--bg-hover)]',
  ghost: 'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] border-transparent',
  danger: 'bg-red-500 text-white hover:bg-red-600 border-transparent',
  'danger-outline': 'bg-transparent text-red-500 border-red-500 hover:bg-red-50',
  white: 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',

  // Accent colors (for CTAs)
  accent: 'bg-blue-500 text-white hover:bg-blue-600 border-transparent',
};

/**
 * Button sizes mapping to Tailwind classes
 */
const sizes = {
  xs: 'py-1 px-2 text-xs',
  sm: 'py-1.5 px-3 text-xs',
  md: 'py-2 px-4 text-sm',
  lg: 'py-3 px-6 text-base',
  xl: 'py-4 px-8 text-lg',
  icon: 'p-2',
};

/**
 * Safe navigate hook - returns null if not in Router context
 */
const useSafeNavigate = () => {
  try {
    return useNavigate();
  } catch {
    return null;
  }
};

/**
 * Main Button Component
 *
 * @param {Object} props
 * @param {'primary'|'secondary'|'outline'|'ghost'|'danger'|'danger-outline'|'white'|'accent'} props.variant
 * @param {'xs'|'sm'|'md'|'lg'|'xl'|'icon'} props.size
 * @param {'button'|'submit'|'reset'} props.type
 * @param {boolean} props.loading
 * @param {boolean} props.disabled
 * @param {boolean} props.fullWidth
 * @param {string} props.icon - Iconify icon name (left side)
 * @param {string} props.iconRight - Iconify icon name (right side)
 * @param {string} props.to - Navigate path (uses react-router)
 * @param {string} props.href - External link
 * @param {string} props.className - Additional custom classes
 * @param {React.ReactNode} props.children
 */
export const Button = forwardRef(function Button({
  children,
  variant = 'primary',
  size = 'md',
  type = 'button',
  loading = false,
  disabled = false,
  fullWidth = false,
  icon,
  iconRight,
  to,
  href,
  className = '',
  onClick,
  ...props
}, ref) {
  const navigate = useSafeNavigate();

  const baseClasses = 'inline-flex items-center justify-center gap-2 border rounded-lg font-medium cursor-pointer transition-all duration-150 outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1';
  const disabledClasses = 'disabled:opacity-50 disabled:cursor-not-allowed';
  const widthClass = fullWidth ? 'w-full' : '';

  const handleClick = (e) => {
    if (disabled || loading) return;

    if (to && navigate) {
      navigate(to);
    } else if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else if (onClick) {
      onClick(e);
    }
  };

  const content = (
    <>
      {loading && (
        <Icon icon="mdi:loading" className="animate-spin text-current" />
      )}
      {!loading && icon && (
        <Icon icon={icon} className="text-current" />
      )}
      {children}
      {iconRight && (
        <Icon icon={iconRight} className="text-current" />
      )}
    </>
  );

  // External link - use <a> tag
  if (href && !disabled && !loading) {
    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${baseClasses} ${variants[variant] || variants.primary} ${sizes[size]} ${widthClass} ${className}`}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={`${baseClasses} ${variants[variant] || variants.primary} ${sizes[size]} ${disabledClasses} ${widthClass} ${className}`}
      disabled={disabled || loading}
      onClick={handleClick}
      {...props}
    >
      {content}
    </button>
  );
});

/**
 * Back Button - For navigating back
 */
export function BackButton({
  to,
  label = '返回',
  className = '',
  ...props
}) {
  const navigate = useSafeNavigate();

  const handleClick = () => {
    if (!navigate) return;
    if (to) {
      navigate(to);
    } else {
      navigate(-1);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-2 bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg text-sm font-medium cursor-pointer transition-all border-none outline-none ${className}`}
      {...props}
    >
      <Icon icon="mdi:arrow-left" className="text-lg" />
      {label}
    </button>
  );
}

/**
 * Link Button - Text-style button for navigation
 */
export function LinkButton({
  children,
  to,
  href,
  icon,
  className = '',
  ...props
}) {
  const navigate = useSafeNavigate();

  const handleClick = () => {
    if (to && navigate) {
      navigate(to);
    } else if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 text-[var(--text-active)] hover:underline bg-transparent border-none cursor-pointer text-sm font-medium transition-colors outline-none ${className}`}
      {...props}
    >
      {icon && <Icon icon={icon} className="text-current" />}
      {children}
    </button>
  );
}

/**
 * Icon Button Component - For buttons with only an icon
 */
export const IconButton = forwardRef(function IconButton({
  icon,
  selected = false,
  size = 'md',
  variant = 'default',
  iconStyle = {},
  className = '',
  ...props
}, ref) {
  const iconSizes = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl',
  };

  const buttonSizes = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };

  const variantClasses = {
    default: selected
      ? 'bg-[var(--button-primary)] text-[var(--button-primary-text)]'
      : 'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]',
    danger: 'bg-transparent text-red-500 hover:bg-red-50',
    primary: 'bg-[var(--button-primary)] text-[var(--button-primary-text)] hover:opacity-90',
  };

  const baseClasses = 'inline-flex items-center justify-center rounded-lg border-none cursor-pointer transition-all duration-150 outline-none';

  return (
    <button
      ref={ref}
      className={`${baseClasses} ${buttonSizes[size]} ${variantClasses[variant]} ${className}`}
      {...props}
    >
      <Icon icon={icon} className={iconSizes[size]} style={iconStyle} />
    </button>
  );
});

/**
 * Icon Text Button - Button with icon and text
 */
export function IconTextButton({
  children,
  icon,
  primary = false,
  iconPosition = 'left',
  iconStyle = {},
  to,
  className = '',
  onClick,
  ...props
}) {
  const navigate = useSafeNavigate();

  const handleClick = (e) => {
    if (to && navigate) {
      navigate(to);
    } else if (onClick) {
      onClick(e);
    }
  };

  const baseClasses = 'inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 outline-none border-none px-3 py-2';
  const variantClasses = primary
    ? 'bg-[var(--button-primary)] text-[var(--button-primary-text)] hover:opacity-90'
    : 'bg-transparent text-[var(--text-primary)] hover:bg-[var(--bg-hover)]';

  const iconElement = <Icon icon={icon} className="text-lg" style={iconStyle} />;

  return (
    <button
      className={`${baseClasses} ${variantClasses} ${className}`}
      onClick={handleClick}
      {...props}
    >
      {iconPosition === 'left' && iconElement}
      {children && <span>{children}</span>}
      {iconPosition === 'right' && iconElement}
    </button>
  );
}

/**
 * Pill Button - Rounded tag/chip style button
 */
export function PillButton({
  children,
  color = 'blue',
  active = false,
  icon,
  className = '',
  ...props
}) {
  const colors = {
    blue: active ? 'bg-blue-500 text-white' : 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20',
    gray: active ? 'bg-gray-500 text-white' : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]',
    green: active ? 'bg-green-500 text-white' : 'bg-green-500/10 text-green-500 hover:bg-green-500/20',
    red: active ? 'bg-red-500 text-white' : 'bg-red-500/10 text-red-500 hover:bg-red-500/20',
    purple: active ? 'bg-purple-500 text-white' : 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20',
    orange: active ? 'bg-orange-500 text-white' : 'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20',
  };

  return (
    <button
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-150 border-none outline-none ${colors[color]} ${className}`}
      {...props}
    >
      {icon && <Icon icon={icon} className="text-current" />}
      {children}
    </button>
  );
}

/**
 * Expandable Button - Button with dropdown options
 */
export function ExpandableButton({
  children,
  options = [],
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}) {
  const [showOptions, setShowOptions] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  if (options.length === 1) {
    const option = options[0];
    return (
      <Button
        variant={variant}
        size={size}
        onClick={option.onClick}
        href={option.href}
        icon={option.icon}
        {...props}
      >
        {children}
      </Button>
    );
  }

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <Button
        variant={variant}
        size={size}
        onClick={() => setShowOptions(!showOptions)}
        className={className}
        {...props}
      >
        {children}
        <Icon icon="mdi:chevron-down" className={`text-base transition-transform ${showOptions ? 'rotate-180' : ''}`} />
      </Button>

      {showOptions && (
        <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg shadow-lg z-50 overflow-hidden">
          {options.map((option, index) => (
            option.href ? (
              <a
                key={index}
                href={option.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors border-b border-[var(--border-color)] last:border-b-0"
                onClick={() => setShowOptions(false)}
              >
                {option.icon && <Icon icon={option.icon} className="text-[var(--text-tertiary)]" />}
                {option.label}
              </a>
            ) : (
              <button
                key={index}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors border-b border-[var(--border-color)] last:border-b-0 text-left bg-transparent border-none cursor-pointer"
                onClick={() => {
                  setShowOptions(false);
                  option.onClick?.();
                }}
              >
                {option.icon && <Icon icon={option.icon} className="text-[var(--text-tertiary)]" />}
                {option.label}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Loading Button - Shows loading state with optional text change
 */
export function LoadingButton({
  children,
  loading = false,
  loadingText = '',
  icon,
  ...props
}) {
  return (
    <Button loading={loading} icon={!loading ? icon : undefined} {...props}>
      {loading ? (loadingText || children) : children}
    </Button>
  );
}

/**
 * Action Button - For toolbar/action bar buttons
 */
export function ActionButton({
  children,
  icon,
  active = false,
  className = '',
  ...props
}) {
  return (
    <button
      className={`inline-flex items-center gap-1.5 px-3 py-2 bg-transparent border-none rounded-md cursor-pointer text-sm transition-colors outline-none ${
        active
          ? 'text-[var(--text-active)] bg-[var(--bg-active)]'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
      } ${className}`}
      {...props}
    >
      {icon && <Icon icon={icon} className="text-lg" />}
      {children}
    </button>
  );
}

/**
 * Close Button - For modals, cards, etc.
 */
export function CloseButton({
  size = 'md',
  className = '',
  ...props
}) {
  const sizeClasses = {
    sm: 'w-6 h-6 text-base',
    md: 'w-8 h-8 text-lg',
    lg: 'w-10 h-10 text-xl',
  };

  return (
    <button
      className={`inline-flex items-center justify-center rounded-full bg-transparent border-none cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors outline-none ${sizeClasses[size]} ${className}`}
      {...props}
    >
      <Icon icon="mdi:close" />
    </button>
  );
}

/**
 * Dropdown Menu - Icon button with dropdown options
 * @param {Object} props
 * @param {string} props.icon - Trigger icon (default: mdi:dots-vertical)
 * @param {Array} props.options - Array of { label, icon, onClick, danger, divider }
 * @param {'sm'|'md'|'lg'} props.size - Button size
 * @param {string} props.className - Additional classes for trigger button
 * @param {'left'|'right'} props.align - Dropdown alignment
 */
export function DropdownMenu({
  icon = 'mdi:dots-vertical',
  options = [],
  size = 'sm',
  className = '',
  align = 'right',
  triggerClassName = '',
  ...props
}) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const alignmentClass = align === 'right' ? 'right-0' : 'left-0';

  return (
    <div ref={menuRef} className={`relative inline-block ${className}`} {...props}>
      <IconButton
        icon={icon}
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        className={triggerClassName}
      />

      {showMenu && (
        <div className={`absolute top-full ${alignmentClass} mt-1 min-w-[160px] bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl shadow-lg z-50 overflow-hidden py-1`}>
          {options.map((option, index) => (
            option.divider ? (
              <div key={index} className="h-px my-1 bg-[var(--border-color)]" />
            ) : (
              <button
                key={index}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left bg-transparent border-none cursor-pointer transition-colors ${
                  option.danger
                    ? 'text-red-500 hover:bg-red-50'
                    : 'text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  option.onClick?.();
                }}
              >
                {option.icon && (
                  <Icon
                    icon={option.icon}
                    className={`text-lg ${option.danger ? 'text-red-500' : 'text-[var(--text-tertiary)]'}`}
                  />
                )}
                {option.label}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}

export default Button;
